import { ReactNode, useMemo } from "react";
import { parseISO } from "date-fns";
import { AuditLogChangeType, HistoryDetailsItem } from "@common/types/auditLog";
import {
    isFieldReviewMeta,
    FieldReviewFieldValue,
} from "@common/types/transactionFieldReview";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { addTimeZoneOffset } from "@helpers/date";
import { getStringField, getNumberField } from "../utils/resourceFields";
import { FieldReviewDetailsRow } from "./fieldReview/fieldReviewDetailsViewConfig";
import { FieldReviewDrawerTable } from "./fieldReview/FieldReviewDrawerTable";
import splitDrawerStyles from "./split/SplitHistoryDrawer.module.scss";

interface FieldReviewDrawerContentProps {
    data: HistoryDetailsItem[];
    footer: ReactNode;
    entityIds?: number[];
}

const buildFieldReviewRows = (
    data: HistoryDetailsItem[],
): FieldReviewDetailsRow[] =>
    data.map((log) => {
        const meta = isFieldReviewMeta(log.metadata) ? log.metadata : null;
        const reviewedField = meta?.reviewedField ?? "";
        const reviewerIds = meta?.memberIds ?? [];
        const fieldValue =
            (meta?.fieldValue as
                | FieldReviewFieldValue
                | FieldReviewFieldValue[]
                | null
                | undefined) ?? null;

        const resourceDate = getStringField(log.resource, "date");
        const resolvedDate = resourceDate
            ? addTimeZoneOffset(parseISO(resourceDate))
            : log.createdAt;

        const bankDescription =
            getStringField(log.resource, "bankDescription") ??
            getStringField(log.resource, "description") ??
            "";

        const financialAccountId = getNumberField(
            log.resource,
            "financialAccountId",
        );
        const amount = getNumberField(log.resource, "amount") ?? 0;

        const parsedTransactionId =
            log.sourceId != null ? Number(log.sourceId) : undefined;
        const transactionId =
            parsedTransactionId != null &&
            !isNaN(parsedTransactionId) &&
            parsedTransactionId > 0
                ? parsedTransactionId
                : undefined;

        const isReviewedBefore = log.changeType === AuditLogChangeType.DELETE;
        const isReviewedAfter = log.changeType === AuditLogChangeType.CREATE;

        return {
            id: log.id,
            date: resolvedDate,
            logDate: log.createdAt,
            entityId: log.entityId,
            transactionId,
            bankDescription,
            reviewedField,
            reviewerIds,
            isReviewedBefore,
            isReviewedAfter,
            financialAccountId,
            amount,
            fieldValue,
            resource: log.resource,
        };
    });

export const FieldReviewDrawerContent = ({
    data,
    footer,
    entityIds,
}: FieldReviewDrawerContentProps) => {
    const rows = useMemo(() => buildFieldReviewRows(data), [data]);

    if (!data.length) {
        return (
            <div className={splitDrawerStyles.emptyContainer}>
                <EmptyState header="No review status changes found" />
            </div>
        );
    }

    return (
        <FieldReviewDrawerTable
            rows={rows}
            entityIds={entityIds}
            footer={footer}
        />
    );
};
